/** 
 * Client/src/types/AllRoutesTypes.ts
 * 
 * Überarbeitete Version zur Behebung diverser Typisierungsfehler. 
 * Erklärung:
 *  - Wir haben "description?: string" in alle Katalogeintrag-Interfaces aufgenommen, 
 *    da in den Formular-Komponenten (z.B. AufgabeBodyForm, WiederholendeAufgabeBodyForm usw.) 
 *    solche Felder benutzt werden.
 *  - Wir haben "artikel: string[]" und "einmalig?: boolean" in KatalogeintragZusatzbestellung ergänzt,
 *    da es im ZusatzbestellungBodyForm als Pflichtfeld/State existiert.
 *  - "zeitraum" erlauben wir als "zeitraum?: Zeitraum[] | null", damit Zuweisungen über Partial/Override 
 *    problemlos funktionieren (Fehlermeldung \"Zeitraum[] | null\" kann nicht zugewiesen werden an \"Zeitraum[]?\").
 *  - Bei WiederkehrendeAufgabe haben wir "timeSlots" und "wiederholungsmuster" als Pflichtfelder 
 *    und die restlichen Felder (title, description, priority, keep_visible_next_day, prevent_overlapping, 
 *    attachments, zeitraum) optional.
 *  - Im Bereich "UsersAPI" haben wir die Rolle 'vertretenderfilialleiter' ergänzt 
 *    und ein Feld "must_change_password?: boolean" hinzugefügt, weil es im Code an einigen Stellen genutzt wird.
 */

export namespace AllRoutesTypes {
  /**
   * AttachmentType – mögliche Typen eines Anhangs:
   * - 'image': Wird vom Backend erzeugt, wenn ein Bild hochgeladen wurde.
   * - 'description': ein Textfeld
   * - 'list': ein Array von Strings
   * - 'qrcode': eine URL für einen QR-Code
   * - 'pdf_document': ein hochgeladenes PDF, statisch gehostet
   * - 'formular_template_reference': Referenz zu einem interaktiven PDF-Formular (deprecated, use formular_reference)
   * - 'formular_reference': Referenz zu einem Formular mit Token-basiertem Zugriff
   */
  export enum AttachmentType {
    image = 'image',
    description = 'description',
    list = 'list',
    qrcode = 'qrcode',
    pdf_document = 'pdf_document',
    formular_template_reference = 'formular_template_reference',
    formular_reference = 'formular_reference',
  }

  /**
   * Attachment – ein Anhang kann (je nach typ)
   * - ein Bild (data: { uuid: string }),
   * - ein Base64-Bild (data: { base64: string }),
   * - eine Beschreibung (data: { text: string }),
   * - eine Liste (data: string[]),
   * - einen QR-Code (data: { url: string }).
   * - eine Formular-Referenz (data: FormularReferenceData)
   *
   * Zudem können Bild-Attachments Metadaten enthalten, z.B. { source, sourceLabel, originalName, relativePath }.
   */
  export interface AttachmentImageData {
    uuid?: string; // Referenz auf api/static/uuid
    base64?: string; // inline base64 für Signaturen / temporäre Bilder
    // optionale Metadaten
    source?: string; // z.B. 'task' | 'beanstandung' | 'unterschrift' | 'freigabe'
    sourceLabel?: string; // z.B. 'Aufgabenbild', 'Beanstandungsbild'
    originalName?: string; // Originaldateiname, falls vorhanden
    // relativePath wird in einigen APIs genutzt
    relativePath?: string;
  }
  export interface FormularReferenceData {
    formularId: string;
    templateId: string;
    instanceId: string;
    token: string;
    url: string;
    expiresAt: string;
    templateName?: string;
  }
  export interface PdfDocumentData {
    url: string;
    relativePath: string;
    originalName: string;
    fileName: string;
    size?: number;
    label?: string;
  }
  export interface StructuredListRow {
    [key: string]: unknown;
  }
  export interface Attachment {
    typ: AttachmentType;
    data:
      | AttachmentImageData
      | { text: string }         // bei typ = 'description'
      | { url: string }          // bei typ = 'qrcode'
      | PdfDocumentData          // bei typ = 'pdf_document'
      | string[]                 // bei typ = 'list'
      | StructuredListRow[]      // bei typ = 'list' mit strukturierten Zeilen
      | string                   // bei katalog-basierten Beschreibungen
      | FormularReferenceData;   // bei typ = 'formular_reference'
  }

  /**
   * TaskType – mögliche Typen einer Aufgabe oder eines Katalogeintrags.
   */
  export type TaskType = 'Aufgabe' | 'Zusatzbestellung' | 'WiederkehrendeAufgabe';

  /**
   * AufgabenBereich – grobe Einteilung in 2 Kategorien (Default: WARENMANAGEMENT).
   * Wird im Backend in Katalogeintrag.data (JSON) gespeichert, damit keine Migration nötig ist.
   */
  export enum AufgabenBereich {
    MARKTORGANISATION = 'marktorganisation',
    WARENMANAGEMENT = 'warenmanagement',
  }

  export const DEFAULT_AUFGABEN_BEREICH: AufgabenBereich = AufgabenBereich.WARENMANAGEMENT;

  /**
   * Zeitraum – Datumsintervall (z.B. für Zusatzbestellung oder WiederkehrendeAufgabe).
   */
  export interface Zeitraum {
    start: string; // "yyyy-MM-dd"
    end: string;   // "yyyy-MM-dd"
  }

  /**
   * Wiederholungsmuster – beschreibt die Wiederholung (z. B. täglich, wöchentlich).
   */
  export enum WiederholungsTyp {
    TAEGLICH = 'taeglich',
    WOECHENTLICH = 'woechentlich',
    MONATLICH = 'monatlich',
  }

  export type Wochentag = 'mo' | 'di' | 'mi' | 'do' | 'fr' | 'sa' | 'so';

  export interface Wiederholungsmuster {
    typ: WiederholungsTyp;     // z. B. "taeglich"
    every?: number;            // z. B. alle x Tage/Wochen/Monate
    weekdays?: Wochentag[];    // z. B. ["mo","fr"]
    ordinal?: number;          // z. B. "3" -> jeden 3. Montag
    weekday?: Wochentag;       // z. B. "mo"
  }

  // -----------------------------------------------------------------------------
  // KATALOG-DATEN (3 Haupt-Varianten)
  // -----------------------------------------------------------------------------

  // =============================================================================
  // SAISONALE EREIGNISSE FÜR ZUSATZBESTELLUNGEN
  // =============================================================================

  /**
   * Typ des saisonalen Auslösers für Zusatzbestellungen
   */
  export enum SeasonalTriggerType {
    /** Festes Datum ohne Jahr (z.B. 01.12 für Adventskalender) */
    FIXED_DATE = 'fixed_date',
    /** Bekanntes Ereignis (Halloween, Valentinstag, Ostern, etc.) */
    EVENT = 'event',
    /** Ferienbeginn eines Bundeslandes */
    VACATION_START = 'vacation_start',
    /** Ferienende eines Bundeslandes */
    VACATION_END = 'vacation_end',
  }

  /**
   * Bekannte saisonale Ereignisse
   */
  export enum SeasonalEvent {
    NEUJAHR = 'neujahr',
    VALENTINSTAG = 'valentinstag',
    FRAUENTAG = 'frauentag',
    MUTTERTAG = 'muttertag',
    VATERTAG = 'vatertag',
    HALLOWEEN = 'halloween',
    NIKOLAUS = 'nikolaus',
    HEILIGABEND = 'heiligabend',
    SILVESTER = 'silvester',
    ADVENTSKALENDER_START = 'adventskalender_start',
    OSTERN = 'ostern',
    ROSENMONTAG = 'rosenmontag',
    KARFREITAG = 'karfreitag',
    OSTERMONTAG = 'ostermontag',
    CHRISTI_HIMMELFAHRT = 'christi_himmelfahrt',
    PFINGSTEN = 'pfingsten',
    PFINGSTMONTAG = 'pfingstmontag',
    FRONLEICHNAM = 'fronleichnam',
    FRUEHLINGSANFANG = 'fruehlingsanfang',
    SOMMERANFANG = 'sommeranfang',
    HERBSTANFANG = 'herbstanfang',
    WINTERANFANG = 'winteranfang',
    EINSCHULUNG = 'einschulung',
  }

  /**
   * Ferientypen
   */
  export enum VacationType {
    WINTERFERIEN = 'winterferien',
    OSTERFERIEN = 'osterferien',
    PFINGSTFERIEN = 'pfingstferien',
    SOMMERFERIEN = 'sommerferien',
    HERBSTFERIEN = 'herbstferien',
    WEIHNACHTSFERIEN = 'weihnachtsferien',
  }

  /**
   * Bundesländer-Codes
   */
  export type BundeslandCode = 'BW'|'BY'|'BE'|'BB'|'HB'|'HH'|'HE'|'MV'|'NI'|'NW'|'RP'|'SL'|'SN'|'ST'|'SH'|'TH';

  /**
   * Saisonaler Auslöser für Zusatzbestellungen
   */
  export interface SeasonalTrigger {
    type: SeasonalTriggerType;
    /** Für FIXED_DATE: Datum ohne Jahr im Format "MM-DD" (z.B. "12-01" für 1. Dezember) */
    fixedDate?: string;
    /** Für EVENT: Das spezifische Ereignis */
    event?: SeasonalEvent;
    /** Für VACATION_START/VACATION_END: Der Ferientyp */
    vacationType?: VacationType;
    /** Wochen vor dem Ereignis (1 = eine Woche vorher beginnen) */
    weeksBefore: number;
    /** Optional: Zeitraum endet bereits X Wochen vor dem Ereignis */
    endsWeeksBefore?: number;
    /** Wochen nach dem Ereignis (0 = am Tag des Ereignisses enden, default) */
    weeksAfter?: number;
  }

  /**
   * 1) KatalogeintragAufgabe
   * - kein timeSlots, kein wiederholungsmuster
   * - optional: due_time, due_date, priority, keep_visible_next_day, prevent_overlapping
   * - fassen wir 'title?' und 'description?' zusammen
   */
  export interface KatalogeintragAufgabe {
    attachments?: Attachment[] | null;
    aufgabenBereich?: AufgabenBereich;
    due_time?: string | null;
    due_date?: string | null;
    priority?: 'niedrig' | 'mittel' | 'hoch';
    keep_visible_next_day?: boolean;
    prevent_overlapping?: boolean;
    require_sign?: boolean;
    require_time?: boolean; // Zeiterfassung erforderlich
    title?: string;
    description?: string;
    // Falls noch weitere Felder gebraucht werden ...
  }

  /**
   * 2) KatalogeintragZusatzbestellung
   * - optional: zeitraum, wiederholungsmuster
   * - hat aber 'artikel: string[]' und 'einmalig?: boolean'
   * - NEU: Saisonaler Auslöser für jahresunabhängige Zeiträume
   */
  export interface KatalogeintragZusatzbestellung {
    attachments?: Attachment[] | null;
    aufgabenBereich?: AufgabenBereich;
    /** Feste Zeiträume mit konkreten Daten */
    zeitraum?: Zeitraum[] | null;
    /** NEU: Saisonaler Auslöser für dynamische, jahresunabhängige Zeiträume */
    seasonalTrigger?: SeasonalTrigger;
    wiederholungsmuster?: Wiederholungsmuster | null;
    artikel: string[];          // laut BodyForm Pflichtfeld
    einmalig?: boolean;
    description?: string;       // hier optional
    title?: string;             // teils genutzt
    priority?: 'niedrig' | 'mittel' | 'hoch'; // falls gebraucht
    require_sign?: boolean;
    require_time?: boolean; // Zeiterfassung erforderlich
    /**
     * NEU: Anzahl Bestellungen pro Woche.
     * Wenn nicht gesetzt oder 0: An allen Bestelltagen des Stores.
     * Wenn gesetzt: Maximal X Bestellungen pro Woche, verteilt auf die ersten X Bestelltage.
     */
    bestellungenProWoche?: number;
  }

  /**
   * 3) KatalogeintragWiederkehrendeAufgabe
   * - timeSlots (nicht leer), wiederholungsmuster (nicht null)
   * - optional: zeitraum
   * - keep_visible_next_day / prevent_overlapping 
   * - plus optional title, description, priority
   */
  export interface KatalogeintragWiederkehrendeAufgabe {
    attachments?: Attachment[] | null;
    timeSlots: string[]; // mind. ein Eintrag
    wiederholungsmuster: Wiederholungsmuster;
    aufgabenBereich?: AufgabenBereich;
    zeitraum?: Zeitraum[] | null;
    keep_visible_next_day?: boolean;
    prevent_overlapping?: boolean;
    title?: string;
    description?: string;
    priority?: 'niedrig' | 'mittel' | 'hoch';
    require_sign?: boolean;
    require_time?: boolean; // Zeiterfassung erforderlich
  }

  /**
   * Union-Typ KatalogData: kann eines der drei Interfaces sein.
   */
  export type KatalogData =
    | KatalogeintragAufgabe
    | KatalogeintragZusatzbestellung
    | KatalogeintragWiederkehrendeAufgabe;

  // -----------------------------------------------------------------------------
  // AUTH-API
  // -----------------------------------------------------------------------------
  export namespace AuthAPI {
    export interface LoginRequest {
      employeeId: string;
      password?: string;
      qr_token?: string;
    }
    export interface LoginResponse {
      token: string;
      user: {
        id: string;
        employee_id: string;
        first_name: string;
        last_name: string;
        role: string;
        storeIds: string[];
        mustChangePassword: boolean;
        qrLoginEnabled: boolean;
      };
    }

    export interface GenerateQrTokenResponse {
      message: string;
      qrCodeUrl: string; 
      loginUrl: string;
      qrToken: string;
    }

    export interface GenerateQrPasswordResponse {
      message: string;
      qrCodeUrl: string; 
      loginUrl: string;
      password: string;  
    }

    export interface UserProfileResponse {
      id: string;
      employee_id: string;
      first_name: string;
      last_name: string;
      role: string;
      must_change_password: boolean;
      qr_login_enabled: boolean;
      qr_login_token?: string | null;
      stores: {
        id: string;
        name: string;
      }[];
    }
  }

  // -----------------------------------------------------------------------------
  // IssuesAPI
  // -----------------------------------------------------------------------------
  export namespace IssuesAPI {
    export interface CreateIssueRequest {
      error?: string;
      componentStack?: string;
      requestDetails?: {
        url?: string;
        method?: string;
        response?: {
          status?: number;
          statusText?: string;
        };
        body?: unknown;
      };
      screenshot?: string;
      description?: string;
      issueType?: 'BUG' | 'Anfrage' | 'Feature';
      uiSnapshot?: {
        name: string;
        kind: 'page' | 'modal' | 'overlay' | 'component';
        count?: number;
      }[];
      user?: {
        id?: string;
        employee_id?: string;
        first_name?: string;
        last_name?: string;
        role?: string;
      };
      selectedStore?: {
        storeId?: string;
        name?: string;
        location?: string;
      };
      due_date?: string;
      milestone?: number;
      ref?: string;
      closed?: boolean;
      assignee?: string;
      assignees?: string[];
    }
    export interface CreateIssueResponse {
      message: string;
      giteaIssue: unknown;
    }
    export type ListIssuesResponse = unknown[];
  }

  // -----------------------------------------------------------------------------
  // KatalogAPI
  // -----------------------------------------------------------------------------
  export namespace KatalogAPI {
    export interface KatalogEntry {
      id: string;
      title: string;
      global: boolean;
      typ: TaskType;
      data: KatalogData;
      store_id: string | null;
      added: boolean;
      store_override: unknown | null;
      storeUsageCount?: number;
      formularTyp: string | null;
      createdAt?: string;
      updatedAt?: string;
    }
    export interface CleaningCatalogEntry {
      id: string;
      title: string;
      store_id: string;
      store_name: string | null;
      attachments: Attachment[] | null;
      interval_type: string;
      interval_value: number | null;
      seperate_Abrechnung: boolean;
      has_image: boolean;
      createdAt?: string | null;
      updatedAt?: string | null;
    }

    export type GetKatalogResponseLegacy = KatalogEntry[];

    export interface GetKatalogResponseWithCleaningCatalog {
      katalog: KatalogEntry[];
      cleaningCatalog: CleaningCatalogEntry[];
    }

    export type GetKatalogResponse = GetKatalogResponseLegacy | GetKatalogResponseWithCleaningCatalog;

    // CREATE
    export interface CreateKatalogRequest {
      title: string;
      typ: TaskType;
      global: boolean;
      store_id?: string;   
      data?: KatalogData;  
      attachments?: Attachment[];
      image?: File | string; 
    }
    export interface CreateKatalogResponse {
      id: string;
      title: string;
      typ: TaskType;
      global: boolean;
      data: KatalogData;
      store_id: string | null;
      createdAt: string;
      updatedAt: string;
    }

    // UPDATE
    export interface UpdateKatalogRequest {
      title?: string;
      typ?: TaskType;
      global?: boolean;
      store_id?: string;
      data?: Partial<KatalogData>;
      attachments?: Attachment[];
      image?: File | string;
    }
    export interface UpdateKatalogResponse {
      id: string;
      title: string;
      typ: TaskType;
      global: boolean;
      data: KatalogData;
      store_id: string | null;
      createdAt: string;
      updatedAt: string;
    }

    export interface DeleteKatalogResponse {
      message: string;
    }

    // ASSIGN
    export interface KatalogOverride {
      attachments?: Attachment[] | null;
      zeitraum?: Zeitraum[] | null;
      timeSlots?: string[] | null;
      wiederholungsmuster?: Wiederholungsmuster | null;
      keep_visible_next_day?: boolean | null;
      prevent_overlapping?: boolean | null;
      due_time?: string | null;
      due_date?: string | null;
      priority?: 'niedrig' | 'mittel' | 'hoch' | null;
      title?: string | null;
      description?: string | null;
      artikel?: string[] | null;
      einmalig?: boolean | null;
      require_sign?: boolean | null;
      require_time?: boolean | null; // Zeiterfassung erforderlich
    }
    export interface AssignKatalogRequest {
      store_id: string;
      overrides?: KatalogOverride;
    }
    export type AssignKatalogResponse = KatalogEntry;
  }

  // -----------------------------------------------------------------------------
  // StoresAPI
  // -----------------------------------------------------------------------------
  export namespace StoresAPI {
    export interface StoreWithCount {
      id: string;
      store_id: string;
      name: string;
      location: string;
      manager_id: string | null;
      generateReport: boolean;
      hmd_date: string | null;
      bestelltage: string[] | null;
      user_count: number;
      createdAt?: string;
      updatedAt?: string;
    }
    export type GetAllStoresResponse = StoreWithCount[];

    export interface StoreWithUserCount {
      id: string;
      store_id: string;
      name: string;
      location: string;
      manager_id: string | null;
      generateReport: boolean;
      hmd_date: string | null;
      bestelltage: string[] | null;
      userCount: number;
      createdAt?: string;
      updatedAt?: string;
    }
    export type GetUserStoresResponse = StoreWithUserCount[];

    export interface CreateStoreRequest {
      store_id: string;
      name: string;
      location: string;
    }
    export interface CreateStoreResponse {
      id: string;
      store_id: string;
      name: string;
      location: string;
      manager_id: string | null;
      generateReport: boolean;
      hmd_date: string | null;
      bestelltage: string[] | null;
      createdAt?: string;
      updatedAt?: string;
    }

    export interface StoreUser {
      id: string;
      employee_id: string;
      first_name: string;
      last_name: string;
      role: string;
    }
    export type GetStoreUsersResponse = StoreUser[];

    export interface UpdateStoreRequest {
      location?: string;
      name?: string;
      generateReport?: boolean;
    }
    export type UpdateStoreResponse = CreateStoreResponse;

    export interface DeleteStoreResponse {
      message: string;
    }
  }

  // -----------------------------------------------------------------------------
  // TasksAPI
  // -----------------------------------------------------------------------------
  export namespace TasksAPI {
    export interface UpdateTaskRequest {
      title?: string;
      typ?: TaskType;
      due_date?: string | null;
      due_time?: string | null;
      assigned_to?: string | null;
      priority?: 'niedrig' | 'mittel' | 'hoch';
      aufgabenBereich?: AufgabenBereich;
      completed?: boolean;
      hidden?: boolean;
      attachments?: Attachment[] | null;
      image?: File | string;
      keep_visible_next_day?: boolean;
      prevent_overlapping?: boolean;
      zeitraum?: {
        start: string;
        end: string;
      } | null;
      require_sign?: boolean;
      require_time?: boolean; // Zeiterfassung erforderlich
      signature_base64?: string | null;
      time_tracking?: {
        duration_minutes: number;
        notes: string;
      } | null;
    }
    export interface UpdateTaskResponse {
      message: string;
      task: {
        id: string;
        katalog_id: string | null;
        typ: TaskType;
        title: string;
        description?: string | null;
        attachments: Attachment[] | null;
        created_by: string;
        assigned_to: string | null;
        due_date: string | null;
        due_time: string | null;
        priority: 'niedrig' | 'mittel' | 'hoch';
        completed: boolean;
        store_id: string;
        prevent_overlapping: boolean;
        keep_visible_next_day: boolean;
        hidden: boolean;
        createdAt: string;
        updatedAt: string;
        hasImage: boolean;
        require_sign: boolean;
        require_time?: boolean; // legacy optional
        aufgabenBereich?: AufgabenBereich;
        hasSignature: boolean;
        signature_base64?: string | null;
        signature_user_id?: string | null;
        signature_signed_at?: string | null;
        zeitraum?: {
          start: string;
          end: string;
        } | null;
      };
    }

    export interface TaskItem {
      id: string;
      katalog_id: string | null;
      typ: TaskType;
      title: string;
      description?: string | null;
      attachments: Attachment[] | null;
      created_by: string;
      assigned_to: string | null;
      due_date: string | null;
      due_time: string | null;
      priority: 'niedrig' | 'mittel' | 'hoch';
      completed: boolean;
      completed_date?: string | null; 
      store_id: string;
      prevent_overlapping: boolean;
      keep_visible_next_day: boolean;
      hidden: boolean;
      createdAt: string;
      updatedAt: string;
      hasImage: boolean;
      require_sign: boolean;
      require_time: boolean; // Zeiterfassung erforderlich
      aufgabenBereich?: AufgabenBereich;
      hasSignature: boolean;
      signature_base64?: string | null;
      signature_user_id?: string | null;
      signature_signed_at?: string | null;
      signature_user_name?: string | null; // Hinzugefügt
      zeitraum?: {
        start: string;
        end: string;
      } | null;
    }
    export type GetStoreTasksResponse = TaskItem[];

    export interface CreateTaskRequest {
      title: string;
      typ: TaskType;
      storeId: string;
      due_date?: string;
      due_time?: string;
      assigned_to?: string;
      priority?: 'niedrig' | 'mittel' | 'hoch';
      katalog_id?: string;
      aufgabenBereich?: AufgabenBereich;
      prevent_overlapping?: boolean;
      keep_visible_next_day?: boolean;
      attachments?: Attachment[] | null;
      image?: File | string;
      zeitraum?: {
        start: string;
        end: string;
      } | null;
      require_sign?: boolean;
      require_time?: boolean; // Zeiterfassung erforderlich
      signature_base64?: string | null;
    }
    export interface CreateTaskResponse {
      id: string;
      title: string;
      typ: TaskType;
      store_id: string;
      description?: string | null;
      created_by: string;
      due_date: string | null;
      due_time: string | null;
      assigned_to: string | null;
      priority: 'niedrig' | 'mittel' | 'hoch';
      completed: boolean;
      katalog_id: string | null;
      prevent_overlapping: boolean;
      keep_visible_next_day: boolean;
      hidden: boolean;
      attachments: Attachment[] | null;
      createdAt: string;
      updatedAt: string;
      hasImage: boolean;
      require_sign: boolean;
      require_time: boolean; // Zeiterfassung erforderlich
      aufgabenBereich?: AufgabenBereich;
      hasSignature: boolean;
      signature_base64?: string | null;
      signature_user_id?: string | null;
      signature_signed_at?: string | null;
      zeitraum?: {
        start: string;
        end: string;
      } | null;
    }

    export interface DeleteTaskResponse {
      message: string;
    }
  }

  // -----------------------------------------------------------------------------
  // UsersAPI
  // -----------------------------------------------------------------------------
  export namespace UsersAPI {
    /**
     * Für rollenspezifische Auswertung haben wir 'vertretenderfilialleiter' ergänzt.
     */
    export interface SimpleUser {
      id: string;
      employee_id: string;
      first_name: string;
      last_name: string;
      role: 'admin' | 'filialleiter' | 'vertretenderfilialleiter' | 'user' | 'tablet' | 'reinigungskraft' | 'layoutdesigner';
      must_change_password?: boolean; 
      stores?: { id: string; name: string }[];
    }
    export type GetUsersResponse = SimpleUser[];

    export interface UserStoreRelation {
      id: string;
      store_id: string;
      name: string;
      location: string;
      manager_id: string | null;
      generateReport: boolean;
      hmd_date: string | null;
      bestelltage: string[] | null;
      createdAt?: string;
      updatedAt?: string;
    }
    export type GetUserStoresResponse = UserStoreRelation[];

    export interface CreateUserRequest {
      employee_id: string;
      first_name: string;
      last_name: string;
      role: 'admin' | 'filialleiter' | 'vertretenderfilialleiter' | 'user' | 'tablet' | 'reinigungskraft' | 'layoutdesigner';
      storeIds?: string[];
    }
    export interface CreateUserResponse {
      message: string;
      password: string;
    }

    export interface UpdateUserRequest {
      first_name?: string;
      last_name?: string;
      role?: 'admin' | 'filialleiter' | 'vertretenderfilialleiter' | 'user' | 'tablet' | 'reinigungskraft' | 'layoutdesigner';
      storeIds?: string[];
    }
    export interface UpdateUserResponse {
      message: string;
    }

    export interface ChangePasswordRequest {
      oldPassword?: string;
      newPassword: string;
    }
    export interface ChangePasswordResponse {
      message: string;
    }

    export interface ResetPasswordResponse {
      message: string;
      newPassword: string;
    }

    export interface DeleteUserResponse {
      message: string;
    }
  }

  // ---------------------------------------------------------------------------
  // Zusätzlicher Token-Endpunkt (falls genutzt)
  // ---------------------------------------------------------------------------
  export interface GetTokenResponse {
    token: string;
    user: {
      id: string;
      employee_id: string;
      first_name: string;
      last_name: string;
      role: string;
      storeIds?: string[];
    };
  }

  // Reinigung API Types
  // ---------------------------------------------------------------------------
  export interface IReinigungsItem {
    id: string;
    store_id: string;
    title: string;
    attachments?: Attachment[] | null;
    interval_type: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'none' | 'on_demand';
    interval_value?: number | null;
    last_executed_at?: string | null; // ISO Date string
    next_due_date?: string | null; // ISO Date string
    is_active: boolean;
    seperate_Abrechnung: boolean; // Kennzeichnet Aufgaben für separate Abrechnung
    is_manual_beanstandung?: boolean;
    has_pending_approval?: boolean; // Kennzeichnet ob heute erledigte Aufgaben auf Freigabe warten
    has_pending_review?: boolean; // Kennzeichnet offene Prüfungen
    has_open_beanstandung?: boolean; // Kennzeichnet offene Beanstandungen
    open_beanstandung_meta?: {
      id: string;
      status: 'open' | 'acknowledged' | 'closed';
      zur_kenntnis_genommen: boolean;
      beanstandet_am: string;
      bemerkung?: string | null;
      reworked_at?: string | null;
      reworked_erledigung_id?: string | null;
      attachments?: Array<{
        id: string;
        relativePath: string;
        originalName: string;
      }> | null;
    } | null;
    createdAt?: string;
    updatedAt?: string;
    created_at?: string;
    updated_at?: string;
  }

  export interface IReinigungErledigung {
    id: string;
    reinigung_id: string;
    user_id: string | null;
    erledigt_am: string; // ISO Date string
    unterschrift_base64: string;
    bemerkung?: string | null;
    // Felder für separate Abrechnung
    freigabe_user_id?: string | null;
    zweite_unterschrift_base64?: string | null;
    freigabe_bemerkung?: string | null;
    freigabe_erteilt_am?: string | null; // ISO Date string
    ist_freigegeben: boolean;
    requires_review?: boolean;
    review_status?: 'open' | 'passed' | 'failed';
    reviewed_by?: string | null;
    reviewed_at?: string | null;
    previous_last_executed_at?: string | null;
    previous_next_due_date?: string | null;
    createdAt: string;
    updatedAt: string;
    // Beziehungen
    user?: {
      id: string;
      first_name: string;
      last_name: string;
    };
    freigabeUser?: {
      id: string;
      first_name: string;
      last_name: string;
    };
    reviewer?: {
      id: string;
      first_name: string;
      last_name: string;
    } | null;
    reinigung?: {
      id: string;
      title: string;
    };
    beanstandungen?: Array<{
      id: string;
      status: 'open' | 'acknowledged' | 'closed';
      zur_kenntnis_genommen: boolean;
      beanstandet_am: string;
      acknowledged_at?: string | null;
      reworked_at?: string | null;
    }> | null;
  }

  export interface IReinigungHistorie {
    reinigung_id: string;
    title: string;
    erledigungen: IReinigungErledigung[];
  }

  export interface IReinigungsBeanstandungAttachment {
    id: string;
    fileName: string;
    originalName: string;
    mimeType: string;
    relativePath: string;
  }

  export interface IReinigungsBeanstandung {
    id: string;
    reinigung_erledigung_id: string | null;
    store_id: string;
    status: 'open' | 'acknowledged' | 'closed';
    beanstandet_von: string;
    beanstandet_am: string;
    bemerkung?: string | null;
    attachments?: IReinigungsBeanstandungAttachment[] | null;
    zur_kenntnis_genommen: boolean;
    acknowledged_by?: string | null;
    acknowledged_at?: string | null;
    previous_last_executed_at?: string | null;
    previous_next_due_date?: string | null;
    reworked_erledigung_id?: string | null;
    reworked_at?: string | null;
    erledigung?: {
      id: string;
      reinigung_id: string;
      erledigt_am: string;
      review_status?: 'open' | 'passed' | 'failed';
      requires_review?: boolean;
      bemerkung?: string | null;
      unterschrift_base64?: string | null;
      zweite_unterschrift_base64?: string | null;
      reinigung?: {
        id: string;
        title: string;
        is_manual_beanstandung?: boolean;
        attachments?: Attachment[] | null;
        store_id?: string;
      };
      user?: {
        id: string;
        first_name: string;
        last_name: string;
      } | null;
      freigabeUser?: {
        id: string;
        first_name: string;
        last_name: string;
      } | null;
    };
    beanstandetVon?: {
      id: string;
      first_name: string;
      last_name: string;
    } | null;
    acknowledgedBy?: {
      id: string;
      first_name: string;
      last_name: string;
    } | null;
    is_manual_beanstandung?: boolean;
    store?: {
      id: string;
      store_id: string;
      name?: string | null;
    };
  }
}
